import React, { useState, useEffect, useRef } from 'react';
import { AsYouType, parsePhoneNumber, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import styles from './MobileInput.module.css';

export default function MobileInput({
  value,
  onChange,
  disabled,
  required,
  id,
  className,
  settings, 
}) {
  // Respecting the admin's 'cardsFields' config for the mobile element
  const defaultCountry = settings?.defaultCountry || 'ZA';
  const formatStorage = settings?.formatStorage || 'e164';

  const [country, setCountry] = useState(defaultCountry);
  const [displayValue, setDisplayValue] = useState("");
  const inputRef = useRef(null);
  
  // Sort countries alphabetically for an intuitive dropdown UX
  const countries = getCountries().sort((a, b) => a.localeCompare(b));

  // Render initialization without stomping on the user's active cursor placement
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    if (!value) {
      setDisplayValue("");
      return;
    }
    
    try {
      const pn = parsePhoneNumber(value, { defaultCountry });
      if (pn && pn.isValid()) {
        setCountry(pn.country || defaultCountry);
        setDisplayValue(pn.formatNational());
      } else {
        setDisplayValue(value);
      }
    } catch (e) {
      setDisplayValue(value);
    }
  }, [value, defaultCountry]);

  const handleChange = (e) => {
    let raw = e.target.value;
    const callingCode = getCountryCallingCode(country);
    let numericOnly = raw.replace(/[^\d+]/g, '');

    // Allow user to naturally enter international numbers without needing to explicitly add '+'
    if (!numericOnly.startsWith('+') && numericOnly.startsWith(callingCode) && numericOnly.length > callingCode.length + 5) {
       numericOnly = '+' + numericOnly;
    }

    const formatter = new AsYouType(country);
    const formatted = formatter.input(numericOnly);

    // If the AsYouType logic detects a country code mismatch (e.g. typing +1), automatically change the select
    if (formatter.getCountry() && formatter.getCountry() !== country) {
      setCountry(formatter.getCountry());
    }

    setDisplayValue(formatted); // Optimistic UI update

    // Enforce platform storage constraint mapping
    try {
      const pn = parsePhoneNumber(formatted, { defaultCountry: country });
      if (pn && pn.isValid()) {
         let out = "";
         if (formatStorage === 'international') out = pn.formatInternational();
         else if (formatStorage === 'national') out = pn.formatNational();
         else out = pn.format('E.164');
         onChange(out);
      } else {
         onChange(formatted); // Partial entry
      }
    } catch (err) {
      onChange(formatted); // Unresolved partial entry
    }
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    const numericOnly = displayValue.replace(/[^\d+]/g, '');
    
    if (numericOnly) {
       const formatter = new AsYouType(newCountry);
       const formatted = formatter.input(numericOnly);
       setDisplayValue(formatted);
       
       try {
         const pn = parsePhoneNumber(formatted, { defaultCountry: newCountry });
         if (pn && pn.isValid()) {
            let out = "";
            if (formatStorage === 'international') out = pn.formatInternational();
            else if (formatStorage === 'national') out = pn.formatNational();
            else out = pn.format('E.164');
            onChange(out);
         } else {
            onChange(formatted);
         }
       } catch (err) {
         onChange(formatted);
       }
    }
  };

  return (
    <div className={`${styles.container} ${className || ""}`} data-required={required ? "true" : undefined}>
      <div className={styles.selectWrapper}>
        <select 
          className={styles.countrySelect} 
          value={country} 
          onChange={handleCountryChange}
          disabled={disabled}
          title="Country Code Select"
        >
          {countries.map(c => (
            <option key={c} value={c}>{c} (+{getCountryCallingCode(c)})</option>
          ))}
        </select>
      </div>
      <input
        ref={inputRef}
        id={id}
        type="tel"
        className={styles.phoneInput}
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        placeholder={country === 'ZA' ? '012 345 6789' : 'Number...'}
      />
    </div>
  );
}
